import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Activity, 
  Database,
  Server,
  Globe,
  Settings,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { seoDbService } from '@/services/seoDbService';
import { seoFetchService } from '@/services/seoFetchService';
import { supabase } from '@/integrations/supabase/client';

interface HealthStatus {
  component: string;
  status: 'healthy' | 'warning' | 'error';
  message: string;
  details?: string;
}

const SEOHealthCheck: React.FC = () => {
  const [healthStatuses, setHealthStatuses] = useState<HealthStatus[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [overallHealth, setOverallHealth] = useState<'healthy' | 'warning' | 'error'>('healthy');

  const runHealthCheck = async () => {
    setIsChecking(true);
    const statuses: HealthStatus[] = [];

    try {
      // 1. Check database connectivity
      try {
        const { data, error } = await supabase.from('seo_metrics').select('id').limit(1);
        if (error) throw error;
        statuses.push({
          component: 'Database Connection',
          status: 'healthy',
          message: 'Connected successfully',
          details: 'Supabase database is accessible'
        });
      } catch (error: any) {
        statuses.push({
          component: 'Database Connection',
          status: 'error',
          message: 'Connection failed',
          details: error?.message || 'Unknown database error'
        });
      }

      // 2. Check SEO tables exist and structure
      try {
        // Check each SEO table individually
        const { error: metricsError } = await supabase.from('seo_metrics').select('*').limit(1);
        const { error: issuesError } = await supabase.from('seo_issues').select('*').limit(1);
        const { error: configError } = await supabase.from('seo_monitoring_config').select('*').limit(1);
        const { error: reportsError } = await supabase.from('seo_reports').select('*').limit(1);
        
        if (metricsError || issuesError || configError || reportsError) {
          const errors = [metricsError, issuesError, configError, reportsError].filter(Boolean);
          throw new Error(`Table errors: ${errors.map(e => e?.message).join(', ')}`);
        }
        
        statuses.push({
          component: 'Database Schema',
          status: 'healthy',
          message: 'All SEO tables exist',
          details: 'seo_metrics, seo_issues, seo_monitoring_config, seo_reports'
        });
      } catch (error: any) {
        statuses.push({
          component: 'Database Schema',
          status: 'error',
          message: 'Schema issues detected',
          details: error?.message || 'Table structure problems'
        });
      }

      // 3. Check SEO fetch service
      try {
        const testResult = await seoFetchService.fetchSitemap();
        if (testResult.total > 0) {
          statuses.push({
            component: 'SEO Fetch Service',
            status: 'healthy',
            message: `Sitemap accessible (${testResult.total} URLs)`,
            details: 'Edge function responding correctly'
          });
        } else {
          statuses.push({
            component: 'SEO Fetch Service',
            status: 'warning',
            message: 'Sitemap returned empty',
            details: 'Service works but no URLs found'
          });
        }
      } catch (error: any) {
        statuses.push({
          component: 'SEO Fetch Service',
          status: 'error',
          message: 'Service unavailable',
          details: error?.message || 'Edge function not responding'
        });
      }

      // 4. Check data storage functionality
      try {
        const summary = await seoDbService.getDashboardSummary();
        const hasData = summary.pagesAnalyzed > 0;
        
        statuses.push({
          component: 'Data Storage',
          status: hasData ? 'healthy' : 'warning',
          message: hasData ? 'SEO data present' : 'No SEO data stored',
          details: `${summary.pagesAnalyzed} pages analyzed, ${summary.openIssues} issues tracked`
        });
      } catch (error: any) {
        statuses.push({
          component: 'Data Storage',
          status: 'error',
          message: 'Storage check failed',
          details: error?.message || 'Cannot access stored data'
        });
      }

      // 5. Check monitoring configuration
      try {
        const { data, error } = await supabase
          .from('seo_monitoring_config')
          .select('*')
          .eq('monitoring_enabled', true);
        
        if (error) throw error;
        
        const activeConfigs = data?.length || 0;
        statuses.push({
          component: 'Monitoring Config',
          status: activeConfigs > 0 ? 'healthy' : 'warning',
          message: activeConfigs > 0 ? `${activeConfigs} active configs` : 'No monitoring configured',
          details: activeConfigs > 0 ? 'Automated monitoring enabled' : 'Consider setting up monitoring patterns'
        });
      } catch (error: any) {
        statuses.push({
          component: 'Monitoring Config',
          status: 'error',
          message: 'Config check failed',
          details: error?.message || 'Cannot access monitoring settings'
        });
      }

      setHealthStatuses(statuses);
      
      // Determine overall health
      const hasErrors = statuses.some(s => s.status === 'error');
      const hasWarnings = statuses.some(s => s.status === 'warning');
      
      if (hasErrors) {
        setOverallHealth('error');
        toast.error('SEO system health issues detected');
      } else if (hasWarnings) {
        setOverallHealth('warning');
        toast.warning('SEO system has some warnings');
      } else {
        setOverallHealth('healthy');
        toast.success('SEO system is healthy');
      }

    } catch (error: any) {
      console.error('Health check failed:', error);
      toast.error('Health check failed', { description: error?.message });
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    runHealthCheck();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'error': return <XCircle className="h-4 w-4 text-red-600" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
      case 'warning': return <Badge variant="outline" className="border-yellow-500 text-yellow-700">Warning</Badge>;
      case 'error': return <Badge variant="destructive">Error</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getOverallIcon = () => {
    switch (overallHealth) {
      case 'healthy': return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
      case 'error': return <XCircle className="h-6 w-6 text-red-600" />;
      default: return <Activity className="h-6 w-6" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {getOverallIcon()}
          SEO System Health Check
        </CardTitle>
        <CardDescription>
          Verify all SEO management components are working correctly
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>Overall Status:</span>
            {getStatusBadge(overallHealth)}
          </div>
          <Button onClick={runHealthCheck} disabled={isChecking} variant="outline" size="sm" className="gap-2">
            <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
            {isChecking ? 'Checking...' : 'Run Check'}
          </Button>
        </div>

        {isChecking && (
          <div className="flex items-center gap-3">
            <Progress value={undefined} className="flex-1" />
            <span className="text-sm text-muted-foreground">Running diagnostics...</span>
          </div>
        )}

        <div className="space-y-3">
          {healthStatuses.map((status, index) => (
            <div key={index} className="rounded-lg border p-3">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {getStatusIcon(status.status)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{status.component}</span>
                      {getStatusBadge(status.status)}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {status.message}
                    </div>
                    {status.details && (
                      <div className="text-xs text-muted-foreground mt-1 font-mono">
                        {status.details}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {healthStatuses.length === 0 && !isChecking && (
          <div className="text-center py-6 text-muted-foreground">
            No diagnostics run yet. Click "Run Check" to start.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SEOHealthCheck;